import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import parse from "date-fns/parse";
import { resources } from "@constants/hardcoded-strings";
import {
    getUserSummaryReferenceDataReduxRequest,
    getUserSummaryResultsReduxRequest
} from "@redux-requests/user-summary";
import { UserSummarySearchFiltersState } from "@idw-types/state/user-summary-state";
import { setUserSummarySearchFilters } from "@redux/actions/user-summary-actions";
import { SERVER_DATE_FORMAT } from "@constants/date-formats";
import { TimeRangeTypes } from "@enums/time-range-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { CustomizeBySearchTypes } from "@enums/customize-by-search-types";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { initialUserSummaryState } from "@redux/reducers/user-summary-reducer";
import { extractRelyingPartiesForPropertyName } from "@utils/permissions-util";
import { ContentPageHeader } from "../../common/content-page-header/content-page-header";
import { CardContainer } from "../../common/card-container/card-container";
import { LoadingIndicator } from "../../common/loading-indicator/loading-indicator";
import { ContentPageBody } from "../../common/content-page-body/content-page-body";
import { UserSummarySearchForm } from "./user-summary-search-form/user-summary-search-form";
import { UserSummaryTableContainer } from "./user-summary-table-container/user-summary-table-container";
import { getSessionState, getUserSummaryState } from "@redux/selectors/root-selectors";

export const UserSummaryContainer = () => {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const {
        referenceData,
        searchFilters,
        searchResults
    } = useSelector(getUserSummaryState);
    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getUserSummaryReferenceDataReduxRequest(dispatch)();
        }
    }, [referenceData.isLoading, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.companies &&
            referenceData.relyingParties &&
            referenceData.rpServices &&
            referenceData.workflows
        );
    }, [
        referenceData.companies,
        referenceData.relyingParties,
        referenceData.rpServices,
        referenceData.workflows
    ]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading;
    }, [referenceData.isLoading]);

    const isLoaded = useMemo(() => {
        return !isLoading && isReferenceDataPresent && !shouldLoadReferenceData;
    }, [isLoading, isReferenceDataPresent, shouldLoadReferenceData]);

    const allowedRelyingParties = useMemo(() => {
        if (referenceData.relyingParties) {
            return extractRelyingPartiesForPropertyName(
                UserPermissionTypes.VIEW_WORKFLOW_USAGE_DETAILS,
                session.user.axnAdminUserRPProperties,
                referenceData.relyingParties
            );
        }

        return [];
    }, [
        session.user.axnAdminUserRPProperties,
        referenceData.relyingParties
    ]);

    const allowedWorkflows = useMemo(() => {
        if (referenceData.workflows) {
            const rpIds = allowedRelyingParties.map(rp => rp.id);
            return referenceData.workflows.filter(workflow => rpIds.includes(workflow.relyingPartyID));
        }

        return [];
    }, [
        allowedRelyingParties,
        referenceData.workflows
    ]);


    const allowedServices = useMemo(() => {
        if (referenceData.rpServices) {
            const rpIds = allowedRelyingParties.map(rp => rp.id);
            return referenceData.rpServices.filter(service => rpIds.includes(service.relyingPartyID));
        }

        return [];
    }, [
        allowedRelyingParties,
        referenceData.rpServices
    ]);

    useEffect(() => {
        if (Array.from(searchParams.entries()).length > 0 && isLoaded) {
            const shouldSearchOnLoad = isStringTrue(searchParams.get("shouldSearchOnLoad"));

            const newSearchFilters: UserSummarySearchFiltersState = {
                customizeBySearchType: (searchParams.get("customizeBySearchType") || initialUserSummaryState.searchFilters.customizeBySearchType) as CustomizeBySearchTypes,
                companyId: searchParams.get("companyId") || initialUserSummaryState.searchFilters.companyId,
                rpServiceId: searchParams.get("rpServiceId") || initialUserSummaryState.searchFilters.rpServiceId,
                workflowId: searchParams.get("workflowId") || initialUserSummaryState.searchFilters.workflowId,
                timeRangeType: (searchParams.get("timeRangeType") || initialUserSummaryState.searchFilters.timeRangeType) as TimeRangeTypes,
                startDate: searchParams.has("startDate")
                    ? parse(searchParams.get("startDate"), SERVER_DATE_FORMAT, new Date())
                    : initialUserSummaryState.searchFilters.startDate,
                endDate: searchParams.has("endDate")
                    ? parse(searchParams.get("endDate"), SERVER_DATE_FORMAT, new Date())
                    : initialUserSummaryState.searchFilters.endDate
            };

            if (shouldSearchOnLoad) {
                handleSearch(newSearchFilters);
            } else {
                dispatch(setUserSummarySearchFilters(newSearchFilters));
            }
        }
    }, [searchParams, isLoaded]);

    const handleSearch = async (newSearchFilters: UserSummarySearchFiltersState) => {
        const { companyId, rpServiceId, workflowId, startDate, endDate } = newSearchFilters;
        dispatch(setUserSummarySearchFilters(newSearchFilters));

        getUserSummaryResultsReduxRequest(dispatch)(
            companyId,
            rpServiceId,
            workflowId,
            startDate,
            endDate
        );
    };

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.userSummary.header} />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <UserSummarySearchForm
                        requestInProgress={searchResults.isLoading}
                        companies={referenceData.companies}
                        relyingParties={allowedRelyingParties}
                        rpServices={allowedServices}
                        workflows={allowedWorkflows}
                        searchFilters={searchFilters}
                        handleSearch={handleSearch}
                    />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.monthlyUsersResult && (
                    <UserSummaryTableContainer
                        monthlyUsersResult={searchResults.monthlyUsersResult}
                    />
                )}
                {isLoaded && searchResults.isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};